// Spotify API configuration (client-side PKCE flow — no client secret needed)
export const SPOTIFY_CONFIG = {
  clientId: (process.env.REACT_APP_SPOTIFY_CLIENT_ID || '').trim(),
  redirectUri: (process.env.REACT_APP_SPOTIFY_REDIRECT_URI || '').trim(),
};

export const SPOTIFY_SCOPES = [
  'user-library-read',
  'playlist-modify-public',
  'playlist-modify-private',
  'user-read-private',
  'user-top-read',
];

// Bump this version whenever SPOTIFY_SCOPES changes — forces re-auth
const SCOPE_VERSION = 'v3';

export function checkScopeVersion() {
  const stored = localStorage.getItem('spotify_scope_version');
  if (stored !== SCOPE_VERSION) {
    // Scopes changed — clear any old session data so user re-auths with new scopes
    localStorage.removeItem('spotify_code_verifier');
    localStorage.setItem('spotify_scope_version', SCOPE_VERSION);
  }
}

// ─── PKCE helpers ────────────────────────────────────────────────────
function generateRandomString(length) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => charset[b % charset.length]).join('');
}

async function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
}

function base64url(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export async function getSpotifyAuthUrl() {
  const verifier = generateRandomString(64);
  const challenge = base64url(await sha256(verifier));
  // localStorage is more reliable than sessionStorage across OAuth redirects
  localStorage.setItem('spotify_code_verifier', verifier);

  const params = new URLSearchParams({
    client_id: SPOTIFY_CONFIG.clientId,
    response_type: 'code',
    redirect_uri: SPOTIFY_CONFIG.redirectUri,
    scope: SPOTIFY_SCOPES.join(' '),
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  return `https://accounts.spotify.com/authorize?${params.toString()}`;
}

// ─── Token exchange ───────────────────────────────────────────────────
export async function exchangeCodeForToken(code) {
  const verifier = localStorage.getItem('spotify_code_verifier');

  if (!verifier) {
    throw new Error('Auth session expired — please try connecting again.');
  }

  const res = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: SPOTIFY_CONFIG.redirectUri,
      client_id: SPOTIFY_CONFIG.clientId,
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    console.error('Token exchange error details:', errorBody);
    throw new Error(`Token exchange failed: ${res.status} — ${errorBody.error_description || errorBody.error || 'unknown error'}`);
  }

  const data = await res.json();
  localStorage.removeItem('spotify_code_verifier');
  return data.access_token;
}

// ─── Spotify API helpers ──────────────────────────────────────────────

// Fetch with automatic retry on 429 (respects Retry-After header)
async function spotifyFetch(url, token, retries = 3) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    if (res.status === 429) {
      const wait = parseInt(res.headers.get('Retry-After') || '2', 10) * 1000 + 500;
      console.warn(`Rate limited — waiting ${wait}ms before retry`);
      await new Promise(r => setTimeout(r, wait));
      continue;
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const msg = body?.error?.message || res.statusText;
      throw new Error(`${res.status}: ${msg} — ${url.split('?')[0]}`);
    }

    return res.json();
  }
  throw new Error(`Failed after ${retries} retries — ${url.split('?')[0]}`);
}

// Fetch ALL liked songs — 200ms pause between pages to stay under rate limit
export async function fetchAllLikedSongs(token, onProgress) {
  const songs = [];
  let url = 'https://api.spotify.com/v1/me/tracks?limit=50';
  while (url) {
    const data = await spotifyFetch(url, token);
    songs.push(...data.items.map(i => i.track).filter(Boolean));
    onProgress(`fetching liked songs... ${songs.length} / ${data.total}`);
    url = data.next;
    if (url) await new Promise(r => setTimeout(r, 200));
  }
  return songs;
}

// Map Spotify micro-genre strings → clean display buckets
const GENRE_RULES = [
  ['Hip-Hop',   ['hip hop','rap','trap','drill','phonk','boom bap','crunk','snap','cloud rap']],
  ['R&B',       ['r&b','soul','neo soul','motown','new jack swing','quiet storm']],
  ['Pop',       ['pop','dance pop','electropop','synth-pop','teen pop','bubblegum','power pop']],
  ['Electronic',['electronic','edm','house','techno','dubstep','trance','drum and bass','dnb',
                 'lo-fi','chillwave','future bass','hyperpop','bass music','garage','ambient']],
  ['Rock',      ['rock','punk','grunge','hardcore','post-rock','emo','screamo','post-hardcore',
                 'shoegaze','indie rock','alt-rock','alternative rock']],
  ['Metal',     ['metal','metalcore','death metal','black metal','heavy metal','deathcore']],
  ['Indie',     ['indie','alternative','dream pop','chamber pop','lo-fi indie']],
  ['Classical', ['classical','orchestra','opera','chamber','baroque','symphony','neoclassical']],
  ['Jazz',      ['jazz','bebop','fusion','bossa nova','blues','soul jazz']],
  ['Country',   ['country','bluegrass','americana','folk','singer-songwriter']],
  ['Latin',     ['latin','reggaeton','salsa','cumbia','bachata','dembow','corrido','urbano']],
  ['K-Pop',     ['k-pop','korean','k-rap','k-indie','kpop']],
  ['Afrobeats', ['afrobeats','afropop','afroswing','highlife','amapiano','afrofusion']],
];

function classifyGenre(genres) {
  if (!genres || !genres.length) return null;
  const lower = genres.map(g => g.toLowerCase());
  for (const [bucket, keywords] of GENRE_RULES) {
    if (lower.some(g => keywords.some(k => g.includes(k)))) return bucket;
  }
  return 'Other';
}

export async function sortSongsIntoGenres(songs, token, onProgress) {
  // Count how many songs each artist appears in
  const freq = {};
  songs.forEach(s => s.artists?.forEach(a => {
    if (a.id) freq[a.id] = (freq[a.id] || 0) + 1;
  }));

  // Take the top 50 most frequent artists — one single API call, no rate-limit risk
  const top50 = Object.entries(freq)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 50)
    .map(([id]) => id);

  onProgress('fetching genres for your top artists...');

  const genreMap = {};

  // Two batches of 50 artists — 300ms apart, with retry on 429
  const batches = [top50, Object.entries(freq).sort(([,a],[,b])=>b-a).slice(50,100).map(([id])=>id)];
  for (let b = 0; b < batches.length; b++) {
    const batch = batches[b];
    if (!batch.length) continue;
    if (b > 0) await new Promise(r => setTimeout(r, 300));
    try {
      const data = await spotifyFetch(
        `https://api.spotify.com/v1/artists?ids=${batch.join(',')}`,
        token
      );
      data.artists.forEach(a => { if (a) genreMap[a.id] = a.genres || []; });
    } catch (err) {
      console.warn(`Artist batch ${b + 1} failed:`, err.message);
    }
  }

  onProgress('sorting music...');
  const buckets = {};

  songs.forEach(song => {
    const genres = song.artists?.flatMap(a => genreMap[a.id] || []) || [];
    const bucket = classifyGenre(genres) || 'Other';
    if (!buckets[bucket]) buckets[bucket] = [];
    buckets[bucket].push(song.uri);
  });

  return buckets;
}

// Create playlists and fill them — uses /me/playlists (avoids userId 403)
export async function createGenrePlaylists(buckets, token, onProgress) {
  const results = [];
  const entries = Object.entries(buckets).filter(([, uris]) => uris.length > 0);

  for (let i = 0; i < entries.length; i++) {
    const [genre, uris] = entries[i];
    onProgress(`creating "${genre}" playlist (${i + 1}/${entries.length})...`);

    const createRes = await fetch('https://api.spotify.com/v1/me/playlists', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        name: genre,
        description: `sorted by idonthaveagf`,
        public: false,
      }),
    });

    if (!createRes.ok) {
      const errBody = await createRes.json().catch(() => ({}));
      console.error(`Failed to create playlist "${genre}":`, createRes.status, errBody);
      // Skip this playlist rather than crashing the whole flow
      continue;
    }

    const playlist = await createRes.json();
    if (!playlist.id) {
      console.error(`Playlist "${genre}" created but no ID returned`, playlist);
      continue;
    }

    // Add tracks in batches of 100
    for (let j = 0; j < uris.length; j += 100) {
      await fetch(`https://api.spotify.com/v1/playlists/${playlist.id}/tracks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uris: uris.slice(j, j + 100) }),
      });
    }

    results.push({ genre, count: uris.length, id: playlist.id });
  }
  return results;
}
